import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const getServices = (t) => [
  { 
    id: 'nie', 
    label: t('unifiedCalculator.servicesList.nie.label'), 
    description: t('unifiedCalculator.servicesList.nie.description'),
    min: 150, 
    max: 300, 
    popular: true,
    defaultSelected: true 
  },
  { 
    id: 'initial', 
    label: t('unifiedCalculator.servicesList.initial.label'), 
    description: t('unifiedCalculator.servicesList.initial.description'),
    min: 600, 
    max: 1200, 
    popular: true,
    defaultSelected: true 
  },
  { 
    id: 'tax', 
    label: t('unifiedCalculator.servicesList.tax.label'), 
    description: t('unifiedCalculator.servicesList.tax.description'),
    min: 60, 
    max: 200, 
    popular: true,
    defaultSelected: true 
  },
  { 
    id: 'renewal', 
    label: t('unifiedCalculator.servicesList.renewal.label'), 
    description: t('unifiedCalculator.servicesList.renewal.description'),
    min: 400, 
    max: 800, 
    popular: true,
    defaultSelected: false 
  },
  { 
    id: 'beckham', 
    label: t('unifiedCalculator.servicesList.beckham.label'), 
    description: t('unifiedCalculator.servicesList.beckham.description'),
    min: 100, 
    max: 300, 
    popular: false,
    defaultSelected: false 
  },
  { 
    id: 'citizenship', 
    label: t('unifiedCalculator.servicesList.citizenship.label'), 
    description: t('unifiedCalculator.servicesList.citizenship.description'),
    min: 800, 
    max: 2000, 
    popular: false,
    defaultSelected: false 
  },
  { 
    id: 'longterm', 
    label: t('unifiedCalculator.servicesList.longterm.label'), 
    description: t('unifiedCalculator.servicesList.longterm.description'),
    min: 600, 
    max: 1200, 
    popular: false,
    defaultSelected: false 
  },
  { 
    id: 'regreso', 
    label: t('unifiedCalculator.servicesList.regreso.label'), 
    description: t('unifiedCalculator.servicesList.regreso.description'),
    min: 50, 
    max: 100, 
    popular: false,
    defaultSelected: false 
  },
];

const UnifiedCalculator = ({ isHero = false, className = "", isCompact = false }) => {
  const { t } = useTranslation();
  const services = getServices(t);
  
  // Pre-select common services
  const [selected, setSelected] = useState(
    services.filter(s => s.defaultSelected).map(s => s.id)
  );
  const [consultations, setConsultations] = useState(0);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(!isHero && !isCompact);
  const [animateResult, setAnimateResult] = useState(false);
  const [manuallyCollapsed, setManuallyCollapsed] = useState(false);

  // Show popular services first, others when expanded
  const displayServices = expanded ? services : services.filter(s => s.popular);

  const toggleSelection = (id) => {
    setSelected((prev) => {
      const newSelected = prev.includes(id) 
        ? prev.filter((item) => item !== id) 
        : [...prev, id];
      
      // Trigger animation when selection changes with haptic feedback simulation
      setAnimateResult(true);
      setTimeout(() => setAnimateResult(false), 400);
      
      // Announce change for screen readers
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        const service = services.find(s => s.id === id);
        const action = newSelected.includes(id) ? 'added' : 'removed';
        // Note: Actual implementation would use aria-live regions instead
      }
      
      return newSelected;
    });
  };

  const total = selected.reduce(
    (acc, id) => {
      const item = services.find((o) => o.id === id);
      if (item) {
        acc.min += item.min;
        acc.max += item.max;
      }
      return acc;
    },
    { min: 0, max: 0 }
  );

  const consultationTotal = consultations * 100;
  total.min += consultationTotal;
  total.max += consultationTotal;

  const handleCopy = () => {
    const selectedLabels = selected
      .map((id) => services.find((o) => o.id === id)?.label)
      .filter(Boolean);

    if (consultations > 0) {
      selectedLabels.push(`${consultations} ${consultations > 1 ? t('unifiedCalculator.results.consultationsPlural') : t('unifiedCalculator.results.consultations')}`);
    }

    const summary = `${t('unifiedCalculator.share.summary')}\n\n• ${selectedLabels.join(
      '\n• '
    )}\n\n${t('unifiedCalculator.share.estimatedSavings')} €${total.min.toLocaleString()} – €${total.max.toLocaleString()}\n\n${t('unifiedCalculator.share.seeHowMuch')}`;

    navigator.clipboard.writeText(summary).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Note: Auto-expansion removed to keep calculator collapsed by default in hero mode

  const hasSelections = selected.length > 0 || consultations > 0;

  return (
    <section 
      className={`${className} ${isHero ? '' : 'py-8 lg:py-12 bg-gradient-to-b from-blue-50 to-white'}`}
      itemScope 
      itemType="https://schema.org/WebApplication"
    >
      <div className={isHero ? '' : 'container mx-auto px-4'}>
        <div className={`${isHero ? '' : 'max-w-4xl mx-auto'}`}>
          {!isHero && (
            <>
              {/* SEO-Optimized Header for Landing Section */}
              <div className="text-center mb-8 lg:mb-12">
                <h2 
                  className="text-3xl lg:text-4xl font-bold text-blue-600 mb-4"
                  itemProp="name"
                >
                  {t('unifiedCalculator.title')}
                </h2>
                <p 
                  className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto"
                  itemProp="description"
                >
                  {t('unifiedCalculator.description')}
                </p>
              </div>
            </>
          )}

          {/* Calculator Container */}
          <div className={`bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden ${isHero ? 'w-full' : ''}`}>
            {isHero && !isCompact && (
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-1 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                  {t('unifiedCalculator.header.title')}
                </h3>
                <p className="text-blue-700 text-sm font-medium">
                  {t('unifiedCalculator.header.subtitle')}
                </p>
              </div>
            )}

            <div className={`p-4 ${isHero ? '' : 'lg:p-6'}`}>
              {/* Service Selection Grid */}
              <div className={`${isHero && !expanded ? 'space-y-3' : 'grid lg:grid-cols-2 gap-4 lg:gap-6'} transition-all duration-300 ease-in-out`}>
                
                {/* Left Column - Service Selection */}
                <div>
                  {!isHero && (
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {t('unifiedCalculator.services.selectServices')}
                    </h3>
                  )}
                  
                  <div className={`space-y-2 mb-4 ${isCompact ? 'space-y-1' : ''}`}>
                    {displayServices.map((service) => (
                      <label
                        key={service.id}
                        className={`group flex items-start gap-3 ${isCompact ? 'p-2' : 'p-3'} rounded-lg cursor-pointer transition-all duration-200 border focus-within:ring-2 focus-within:ring-gray-300/50 ${
                          selected.includes(service.id)
                            ? 'bg-gray-50 border-gray-300 shadow-sm'
                            : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                        }`}
                        role="checkbox"
                        aria-checked={selected.includes(service.id)}
                        tabIndex="0"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleSelection(service.id);
                          }
                        }}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          <input
                            type="checkbox"
                            checked={selected.includes(service.id)}
                            onChange={() => toggleSelection(service.id)}
                            className="h-4 w-4 text-blue-600 border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 transform checked:scale-110"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 pr-2">
                              <div className={`font-medium text-gray-800 ${isCompact ? 'text-sm' : 'text-sm'}`}>
                                {service.label}
                              </div>
                              {(expanded || !isHero) && !isCompact && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {service.description}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <div className={`font-semibold bg-white px-2 py-1 rounded-md whitespace-nowrap border border-white ${isCompact ? 'text-xs' : 'text-xs'}`}>
                                Save €{service.min}-{service.max}
                              </div>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}

                    {/* Show More/Less Toggle */}
                    {isHero && (
                      <button
                        onClick={() => {
                          setExpanded(!expanded);
                          if (expanded) {
                            setManuallyCollapsed(true);
                          } else {
                            setManuallyCollapsed(false);
                          }
                        }}
                        className="w-full p-3 text-center text-sm text-blue-600 hover:text-blue-800 font-medium border border-dashed border-blue-300 hover:border-blue-400 rounded-lg transition-colors hover:bg-blue-50"
                      >
                        {expanded ? (
                          <>
                            <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                            </svg>
                            {t('unifiedCalculator.services.showLess')}
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                            +{services.length - displayServices.length} {t('unifiedCalculator.services.showMore')}
                          </>
                        )}
                      </button>
                    )}

                    {/* Additional Consultations - Only in expanded mode */}
                    {expanded && (
                      <div className="border-t border-gray-100 pt-3 mt-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <label className="text-sm font-medium text-gray-700 block">
                              {t('unifiedCalculator.services.legalConsultations')}
                            </label>
                            <div className="text-xs text-gray-500">{t('unifiedCalculator.services.consultationPrice')}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setConsultations(Math.max(0, consultations - 1))}
                              className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                              </svg>
                            </button>
                            <span className="w-10 text-center text-sm font-medium text-gray-700">{consultations}</span>
                            <button
                              onClick={() => setConsultations(Math.min(10, consultations + 1))}
                              className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Results (Desktop) / Below Selection (Mobile) */}
                <div className={`${isHero && !expanded ? '' : 'lg:pl-8'}`}>
                  <div className={isHero ? '' : 'sticky top-4'}>
                    {/* Savings Display */}
                    <div className={`bg-white border-2 border-gray-200 rounded-xl ${isCompact ? 'p-4' : 'p-6'} text-center mb-4 transition-all duration-300 shadow-sm ${animateResult ? 'shadow-lg border-green-300' : ''}`}>
                      <div className={`font-medium text-gray-600 uppercase tracking-wide mb-2 ${isCompact ? 'text-xs' : 'text-sm'}`}>
                        {t('unifiedCalculator.results.yourSavings')}
                      </div>
                      <div className={`font-extrabold mb-2 tracking-tight ${isCompact ? 'text-2xl' : 'text-3xl lg:text-4xl'}`}>
                        {!hasSelections ? (
                          <span className="text-gray-500">{t('unifiedCalculator.results.selectServices')}</span>
                        ) : (
                                                      <span itemProp="price" className="text-green-600">
                              €{total.min.toLocaleString()} – €{total.max.toLocaleString()}
                            </span>
                        )}
                      </div>
                      {hasSelections && (
                        <div className={`text-gray-500 font-medium ${isCompact ? 'text-xs' : 'text-xs'}`}>
                          {t('unifiedCalculator.results.moneyInPocket')}
                        </div>
                      )}
                    </div>

                    {/* Success Message - Only when substantial savings */}
                    {hasSelections && total.min >= 300 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2 text-green-700">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <div className="text-sm font-semibold">{t('unifiedCalculator.results.excellentSavings')}</div>
                        </div>
                      </div>
                    )}

                    {/* Selected Services Summary - Simplified */}
                    {hasSelections && expanded && (
                      <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          {t('unifiedCalculator.results.selected')} ({selected.length + (consultations > 0 ? 1 : 0)}):
                        </h4>
                        <div className="space-y-1 text-xs text-gray-600">
                          {selected.slice(0, 3).map((id) => {
                            const item = services.find((o) => o.id === id);
                            return (
                              <div key={id} className="flex justify-between">
                                <span>• {item?.label}</span>
                                <span className="font-medium">€{item?.min}–€{item?.max}</span>
                              </div>
                            );
                          })}
                          {selected.length > 3 && (
                            <div className="text-gray-500">+ {selected.length - 3} {t('unifiedCalculator.results.moreServices')}</div>
                          )}
                          {consultations > 0 && (
                            <div className="flex justify-between">
                              <span>• {consultations} {consultations > 1 ? t('unifiedCalculator.results.consultationsPlural') : t('unifiedCalculator.results.consultations')}</span>
                              <span className="font-medium">€{consultationTotal}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="space-y-2">
                      {hasSelections && expanded && !isCompact && (
                        <button
                          onClick={handleCopy}
                          className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2 border border-gray-200"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          {copied ? t('unifiedCalculator.actions.copied') : t('unifiedCalculator.actions.shareSummary')}
                        </button>
                      )}
                      
                      <a
                        href="/login"
                        className={`block w-full bg-blue-600 hover:bg-blue-700 text-white ${isCompact ? 'px-4 py-2' : 'px-6 py-3'} rounded-lg transition-colors font-semibold text-center ${isCompact ? 'text-sm' : 'text-sm'} shadow-md hover:shadow-lg border-2 border-blue-500 hover:border-blue-600`}
                      >
                        {t('unifiedCalculator.actions.startFreeTrial')}
                      </a>
                    </div>

                    {/* Trust Indicators */}
                    <div className={`text-center ${isCompact ? 'mt-3' : 'mt-4'}`}>
                      <div className={`flex justify-center items-center gap-4 text-gray-500 ${isCompact ? 'text-xs' : 'text-xs'}`}>
                        <span className="flex items-center gap-1.5 font-medium text-blue-600">
                          <svg className={`text-blue-500 ${isCompact ? 'w-3 h-3' : 'w-4 h-4'}`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {t('unifiedCalculator.trust.freeTrial')}
                        </span>
                        <span className="text-gray-300">•</span>
                        <span className="flex items-center gap-1.5 font-medium text-blue-600">
                          <svg className={`text-blue-500 ${isCompact ? 'w-3 h-3' : 'w-4 h-4'}`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {t('unifiedCalculator.trust.noCreditCard')}
                        </span>
                      </div>
                      
                      <div className={`${isCompact ? 'mt-2' : 'mt-3'}`}>
                        <p className={`text-gray-400 text-center ${isCompact ? 'text-xs' : 'text-xs'}`}>
                          {t('unifiedCalculator.trust.disclaimer')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UnifiedCalculator;
